import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertExecution from '@app/model/alert/AlertExecution';
import React, { useCallback, useEffect, useState } from 'react';
import CompetitorsSoldOutAlertExecutionNotificationOutput
	from '@app/model/alert/output/CompetitorsSoldOutAlertExecutionNotificationOutput';
import CompetitorsSoldOutNotificationRowFragment
	from '@app/site/secured/rateshopper/rates/alert-execution/fragment/competitors-sold-out/CompetitorsSoldOutNotificationRowFragment';
import moment from 'moment/moment';
import _orderBy from 'lodash/orderBy';
import _isEmpty from 'lodash/isEmpty';

type NotificationsGroup = {
	type: 'NEXT_WEEK' | 'MONTH',
	month?: string,
	notificationOutputs: CompetitorsSoldOutAlertExecutionNotificationOutput[]
}

const CompetitorsSoldOutAlertExecutionNotificationFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	alertExecution: AlertExecution,
	notificationOutputs: CompetitorsSoldOutAlertExecutionNotificationOutput[],
	isInDrawer?: boolean
}> = ({ companyHotel, notificationOutputs, isInDrawer }) => {

	const [groups, setGroups] = useState<NotificationsGroup[]>([]);

	const existsInGroup = useCallback((groups: NotificationsGroup[], groupType: 'NEXT_WEEK' | 'MONTH', month?: string) => {
		return groups.find(g => g.type === groupType && g.month === month);
	}, [notificationOutputs]);

	useEffect(() => {
		const nextGroups: NotificationsGroup[] = [];
		const untilDate = moment().add(7, 'days');

		notificationOutputs.forEach(notificationOutput => {
			const statDate = moment(notificationOutput.current.statDate);

			if (statDate.isSameOrBefore(untilDate)) {
				let nextWeekGroup = existsInGroup(nextGroups, 'NEXT_WEEK');
				if (!nextWeekGroup) {
					nextWeekGroup = {
						type: 'NEXT_WEEK',
						notificationOutputs: [],
					};
					nextGroups.push(nextWeekGroup);
				}
				nextWeekGroup.notificationOutputs.push(notificationOutput);
			} else {
				const month = statDate.format('MMMM YY');
				let monthGroup = existsInGroup(nextGroups, 'MONTH', month);
				if (!monthGroup) {
					monthGroup = {
						type: 'MONTH',
						month,
						notificationOutputs: [],
					};
					nextGroups.push(monthGroup);
				}
				monthGroup.notificationOutputs.push(notificationOutput);
			}
		});

		nextGroups.forEach(group => {
			group.notificationOutputs = _orderBy(group.notificationOutputs, item => item.current.statDate, 'asc');
		});

		setGroups(nextGroups);
	}, [notificationOutputs, existsInGroup]);

	return !_isEmpty(groups) ? <>
		{groups.map((group, index) => <section key={`idx_${index}`} className={`mt-8 ${isInDrawer ? 'bg-gray-50' : 'bg-white'} rounded-xl border border-gray-200 p-6 shadow-sm sm:p-8`}>
			<div>
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold leading-6 text-gray-950">
						{group.type === 'NEXT_WEEK' ? 'Next Week' : group.month}
					</h2>
				</div>
			</div>
			<div className="mt-5 flow-root">
				<div className="overflow-x-auto">
					<div className="inline-block min-w-full align-middle">
						<table className="min-w-full divide-y divide-gray-300 text-gray-500">
							<thead>
							<tr>
								<th scope="col"
									className="whitespace-nowrap py-3 pl-0 pr-3 text-left text-sm font-semibold text-gray-950">Date
								</th>
								<th scope="col"
									className="whitespace-nowrap px-2 py-3 text-left text-sm font-semibold text-gray-950">#
								</th>
								<th scope="col"
									className="whitespace-normal px-2 py-3 text-left text-sm font-semibold text-gray-950">Competitors
								</th>
								<th scope="col"
									className="whitespace-nowrap py-3 pl-3 pr-0 text-right text-sm font-semibold text-gray-950">Status
								</th>
							</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
							{group.notificationOutputs.map(notificationOutput => <CompetitorsSoldOutNotificationRowFragment
								companyHotel={companyHotel} key={notificationOutput.current.id}
								notification={notificationOutput.current} />)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
			<p className="mt-5 border-t border-gray-200 pt-4 text-sm leading-6 text-gray-500">
				* Please note that the availability reflects the total number of rooms available for the specific date, based on the integration with your PMS or booking engine. &quot;SO&quot; stands for Sold Out.
			</p>
		</section>)}
	</> : <></>;
};

export default CompetitorsSoldOutAlertExecutionNotificationFragment;
